import React, { memo, useCallback } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { formatInTimeZone } from 'date-fns-tz';
import officialscoresheetStyles from '../../officialscoresheet.module.css';
import { formatAttendanceValue, coachData } from '../shared/utils';
import { convertMillipointsToDisplay } from '../../../../utilities/scoring';
import {
  ScoreSheetLineup,
  ScoreSheetAA,
  ScoreSheetJudgeLineup,
} from '../shared/lineups';
import { ScoreSheetRounds } from '../shared/rounds';
import { Clock } from '../shared/clock';
import { useJudgeAssignment } from '../shared/useJudgeAssignment';

const {
  header,
  row,
  teamName,
  teamNameLabel,
  teamNameTeam,
  matchDetailsShort,
  matchDetailsLong,
  matchDetailsLabel,
  matchDetailsShortField,
  matchDetailsLongField,
  body,
  squadLeft,
  squadRight,
  squadFull,
  footer,
  subFooter,
  signature,
  signatureLabel,
  signatureField,
  signatureNames,
  timestamp,
  teamScore,
  teamScoreLabel,
  teamScoreValue,
  teamScoreValueEmpty,
  rotationScore,
  yellowCard,
} = officialscoresheetStyles;

function ChampionshipsScoresheetHeader({
  team,
  format,
  date,
  timezone,
  location,
  attendance,
  session,
}) {
  return (
    <section className={header}>
      <h1>{format.title}</h1>
      <Row className={row}>
        <Col className={teamName}>
          <div className={teamNameLabel}>Team: </div>
          <div className={teamNameTeam}>
            {(team && team?.forceTitle
              ? `${team?.altName}: ${team?.displayName}`
              : team?.altName) || ''}
            {team?.individuals ? ' (Individuals)' : ''}
          </div>
        </Col>
        <Col className={teamName}>
          <div className={teamNameLabel}>Coach: </div>
          <div className={teamNameTeam}>
            {coachData?.[session.gender]?.[team?.rtnId]?.headCoach ??
              coachData['C1'].name}
          </div>
        </Col>
      </Row>
      <Row className={row}>
        <Col className={matchDetailsShort}>
          <div className={matchDetailsLabel}>Date: </div>
          <div className={matchDetailsShortField}>{`${formatInTimeZone(
            new Date(date),
            timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
            "M'/'dd'/'yyyy"
          )}`}</div>
        </Col>
        <Col className={matchDetailsLong}>
          <div className={matchDetailsLabel}>Location: </div>
          <div className={matchDetailsLongField}>{location}</div>
        </Col>
        <Col className={matchDetailsShort}>
          <div className={matchDetailsLabel}>Attendance: </div>
          <div className={matchDetailsShortField}>
            {formatAttendanceValue(attendance)}
          </div>
        </Col>
      </Row>
    </section>
  );
}

function ChampionshipsScoresheetBody({
  team,
  apparatusArray,
  lineupMax,
  judgesByApparatus,
  gender,
  session,
  editable,
  onRefetch,
}) {
  // Use the judge assignment hook for editable mode
  const { assignJudge, removeJudge } = useJudgeAssignment(session, onRefetch);

  const handleJudgeAssign = useCallback(
    async (judgeIndex, judge, scoreType, apparatus) => {
      try {
        await assignJudge(judge, scoreType, apparatus);
      } catch (err) {
        console.error('Failed to assign judge:', err);
      }
    },
    [assignJudge]
  );

  const handleJudgeRemove = useCallback(
    async (judgeIndex, sessionJudgeAssignmentId, _version) => {
      try {
        await removeJudge(sessionJudgeAssignmentId, _version);
      } catch (err) {
        console.error('Failed to remove judge:', err);
      }
    },
    [removeJudge]
  );

  const lineupMaxes = apparatusArray.map((a, i) => {
    if (team?.individuals) {
      return Math.max(
        team?.events?.[i]?.lineup?.length || lineupMax,
        lineupMax
      );
    }
    return lineupMax;
  });

  return (
    <main className={body}>
      <Row className={row}>
        <Col className={squadLeft} xs={8}>
          {apparatusArray.map((a, i) => (
            <div key={i}>
              <ScoreSheetLineup
                squad={team?.events?.[i]?.lineup}
                showSidebar={true}
                apparatus={a}
                max={lineupMaxes?.[i]}
              />
              <ScoreSheetRounds
                data={!team?.individuals ? team?.events?.[i] : null}
                apparatus={a}
              />
            </div>
          ))}
        </Col>
        <Col className={squadRight} xs={4}>
          {apparatusArray.map((a, i) => (
            <div key={i}>
              <ScoreSheetJudgeLineup
                judges={judgesByApparatus[a]}
                showSidebar={false}
                apparatus={a}
                max={lineupMaxes?.[i]}
                gender={gender}
                editable={editable}
                session={session}
                onJudgeAssign={handleJudgeAssign}
                onJudgeRemove={handleJudgeRemove}
              />
              <Row className={rotationScore}>&nbsp;</Row>
            </div>
          ))}
        </Col>
      </Row>
      <Row>
        <Col className={squadLeft} xs={8}>
          <Row className={teamScore}>
            <Col className={teamScoreLabel}>
              {!!team?.neutral ? 'Team ND: ' : null}
            </Col>
            <Col
              className={!!team?.neutral ? teamScoreValue : teamScoreValueEmpty}
            >
              {!!team?.neutral
                ? convertMillipointsToDisplay(team?.neutral, 1)
                : null}
            </Col>
            <Col className={teamScoreLabel}>Final Score: </Col>
            <Col className={teamScoreValue}>
              {!team?.individuals ? team?.finalScore : null}
            </Col>
          </Row>
        </Col>
        <Col className={squadRight} xs={4}></Col>
      </Row>
      <Row className={row}>
        <Col className={squadFull}>
          <ScoreSheetAA
            data={team?.aa}
            showSidebar={true}
            apparatus={apparatusArray}
            max={lineupMax}
          />
        </Col>
      </Row>
    </main>
  );
}

function ChampionshipsScoresheetFooter({
  team,
  page,
  timezone,
  singleSheetOnly,
  sheetOptionsLength,
}) {
  const copyrightTimePageFooter = (page) => {
    return (
      <section className={subFooter}>
        <Row>
          <Col className={timestamp}>
            <span>
              © {new Date().getFullYear()} Virtius&nbsp;&nbsp;»&nbsp;&nbsp;
            </span>
            <Clock timezone={timezone} />
            <span>
              &nbsp;&nbsp;»&nbsp;&nbsp;Page {singleSheetOnly ? 1 : page} of{' '}
              {singleSheetOnly ? 1 : sheetOptionsLength}
            </span>
          </Col>
        </Row>
      </section>
    );
  };

  return (
    <section className={footer}>
      <section className={subFooter}>
        <Row className={row}>
          <Col className={signature}>
            <div className={signatureLabel}>{`Signature: `}</div>
            <div className={signatureField}></div>
          </Col>
          <Col className={yellowCard}>
            <Form.Check
              type="checkbox"
              label="Yellow Card"
              id="yellowCard"
              checked={!!team?.conductCard}
              readOnly
            />
          </Col>
        </Row>
        <Row className={row}>
          <Col className={signatureNames}>
            <div className={signatureLabel}></div>
          </Col>
          <Col className={signatureNames}>
            <div className={signatureLabel}></div>
          </Col>
        </Row>
      </section>
      {copyrightTimePageFooter(page)}
    </section>
  );
}

export const ChampionshipsScoreSheet = memo(_ChampionshipsScoreSheet);

function _ChampionshipsScoreSheet({
  team,
  page,
  format,
  date,
  timezone,
  location,
  attendance,
  session,
  apparatusArray,
  lineupMax,
  judgesByApparatus,
  singleSheetOnly,
  sheetOptionsLength,
  editable = false,
  onRefetch,
}) {
  return (
    <>
      <ChampionshipsScoresheetHeader
        team={team}
        format={format}
        date={date}
        timezone={timezone}
        location={location}
        attendance={attendance}
        session={session}
      />
      <ChampionshipsScoresheetBody
        team={team}
        apparatusArray={apparatusArray}
        lineupMax={lineupMax}
        judgesByApparatus={judgesByApparatus}
        gender={session.gender}
        session={session}
        editable={editable}
        onRefetch={onRefetch}
      />
      <ChampionshipsScoresheetFooter
        team={team}
        page={page}
        timezone={timezone}
        singleSheetOnly={singleSheetOnly}
        sheetOptionsLength={sheetOptionsLength}
      />
    </>
  );
}
